import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "modal", "overlay", "trigger"]
  static values = {
    searchUrl: String,
    debounceMs: { type: Number, default: 300 }
  }

  connect() {
    // Register keyboard shortcut (Cmd+K / Ctrl+K)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)
    
    // Debounce search input
    this.searchTimeout = null
    
    // Track if modal is open
    this.isOpen = false
    
    // Set search URL if not provided
    if (!this.searchUrlValue) {
      this.searchUrlValue = "/search"
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeydown)
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  handleKeydown(event) {
    // Cmd+K on Mac, Ctrl+K on Windows/Linux
    // Check for both lowercase and uppercase 'k' and 'K'
    if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
      // Don't trigger if user is typing in an input/textarea
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      this.open()
      return
    }
    
    // Escape to close
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault()
      event.stopPropagation()
      this.close()
    }
  }

  open() {
    this.isOpen = true
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      // Focus input after modal opens
      setTimeout(() => {
        if (this.hasInputTarget) {
          this.inputTarget.focus()
          this.inputTarget.select()
        }
      }, 100)
    }
    
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden'
  }

  close() {
    this.isOpen = false
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
    }
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden")
      this.resultsTarget.innerHTML = ""
    }
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.blur()
    }
    
    // Restore body scroll
    document.body.style.overflow = ''
  }

  search(event) {
    const query = event.target.value.trim()
    
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    // If query is empty, hide results
    if (!query) {
      if (this.hasResultsTarget) {
        this.resultsTarget.classList.add("hidden")
        this.resultsTarget.innerHTML = ""
      }
      return
    }
    
    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, this.debounceValue)
  }

  async performSearch(query) {
    if (!this.searchUrlValue) {
      console.error("Search URL not configured")
      return
    }
    
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}&format=json`)
      const data = await response.json()
      
      if (this.hasResultsTarget) {
        this.displayResults(data, query)
      }
    } catch (error) {
      console.error("Search error:", error)
      if (this.hasResultsTarget) {
        this.resultsTarget.innerHTML = `
          <div class="px-4 py-3 text-sm text-seris-gray-500">
            Error performing search. Please try again.
          </div>
        `
        this.resultsTarget.classList.remove("hidden")
      }
    }
  }

  displayResults(data, query) {
    const results = this.resultsTarget
    results.innerHTML = ""
    
    let hasResults = false
    
    // Companies
    if (data.companies && data.companies.length > 0) {
      hasResults = true
      const section = this.createSection("Companies", data.companies, "company")
      results.appendChild(section)
    }
    
    // People
    if (data.people && data.people.length > 0) {
      hasResults = true
      const section = this.createSection("People", data.people, "person")
      results.appendChild(section)
    }
    
    // Personas
    if (data.personas && data.personas.length > 0) {
      hasResults = true
      const section = this.createSection("Personas", data.personas, "persona")
      results.appendChild(section)
    }
    
    // Events
    if (data.events && data.events.length > 0) {
      hasResults = true
      const section = this.createSection("Events", data.events, "event")
      results.appendChild(section)
    }
    
    // No results
    if (!hasResults) {
      results.innerHTML = `
        <div class="px-4 py-8 text-center">
          <p class="text-sm text-seris-gray-500">No results found for "<span class="font-medium">${this.escapeHtml(query)}</span>"</p>
          <p class="text-xs text-seris-gray-400 mt-2">Try searching for companies, people, or events</p>
        </div>
      `
    }
    
    // Add "View all results" link
    if (hasResults) {
      const viewAllLink = document.createElement("div")
      viewAllLink.className = "border-t border-seris-gray-200 px-4 py-3"
      viewAllLink.innerHTML = `
        <a href="/search?q=${encodeURIComponent(query)}" class="flex items-center justify-center text-sm font-medium text-primary-600 hover:text-primary-700">
          View all results for "${this.escapeHtml(query)}"
          <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </a>
      `
      results.appendChild(viewAllLink)
    }
    
    if (hasResults || !hasResults) {
      results.classList.remove("hidden")
    }
  }

  createSection(title, items, type) {
    const section = document.createElement("div")
    section.className = "border-b border-seris-gray-100 last:border-b-0"
    
    const header = document.createElement("div")
    header.className = "px-4 py-2 bg-seris-gray-50 border-b border-seris-gray-100"
    header.innerHTML = `<span class="text-xs font-semibold text-seris-gray-600 uppercase tracking-wide">${title}</span>`
    section.appendChild(header)
    
    const list = document.createElement("div")
    list.className = "py-1"
    
    items.slice(0, 5).forEach(item => {
      const link = this.createResultItem(item, type)
      list.appendChild(link)
    })
    
    section.appendChild(list)
    return section
  }

  createResultItem(item, type) {
    const link = document.createElement("a")
    link.className = "flex items-center px-4 py-3 hover:bg-primary-50 transition-colors cursor-pointer"
    
    let href = "#"
    let title = ""
    let subtitle = ""
    
    switch(type) {
      case "company":
        href = `/companies/${item.slug || item.id}`
        title = item.name || "Unnamed Company"
        subtitle = item.description ? item.description.substring(0, 60) + "..." : ""
        break
      case "person":
        href = `/people/${item.id}`
        title = item.name || "Unnamed Person"
        subtitle = item.job_title || ""
        break
      case "persona":
        href = `/personas/${item.id}`
        title = item.name || "Unnamed Persona"
        subtitle = item.job_title || ""
        break
      case "event":
        href = `/events/${item.id}`
        title = item.name || "Unnamed Event"
        subtitle = item.description ? item.description.substring(0, 60) + "..." : ""
        break
    }
    
    link.href = href
    
    link.innerHTML = `
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-seris-gray-900 truncate">${this.escapeHtml(title)}</p>
        ${subtitle ? `<p class="text-xs text-seris-gray-500 truncate mt-0.5">${this.escapeHtml(subtitle)}</p>` : ""}
      </div>
      <svg class="ml-2 w-5 h-5 text-seris-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
      </svg>
    `
    
    // Close modal when item is clicked
    link.addEventListener("click", () => {
      this.close()
    })
    
    return link
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  handleOverlayClick(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  handleSubmit(event) {
    event.preventDefault()
    const query = this.inputTarget.value.trim()
    if (query) {
      window.location.href = `/search?q=${encodeURIComponent(query)}`
      this.close()
    }
  }
}

